import { isEmpty } from 'lodash';

import { VALIDATION_STEP_LABELS, VALIDATION_STEP_ORDER } from './consts.ts';
import { ValidationStep } from './types.ts';
import { useAppraisifyValidation } from './useAppraisifyValidation.ts';

/**
 * This hook is used to determine navigation permissions for the Appraisify wizard steps.
 * It implements sequential validation where steps must be completed in order.
 * If an error exists in an earlier step, all subsequent steps will be disabled.
 * If an error exists in a later step, previous steps remain enabled.
 *
 * @returns Navigation control functions for the appraisify workflow
 * @example
 * const { isStepEnabled } = useAppraisifyNavigation();
 * ...
 * if (isStepEnabled('Analysis')) navigateToStep('Analysis');
 */
export const useAppraisifyNavigation = () => {
  const { errors } = useAppraisifyValidation();

  /**
   * Determines if a step has validation errors.
   */
  const hasStepErrors = (step: ValidationStep) => !isEmpty(errors[step]);

  /**
   * Gets the first step that has errors, or null if no errors exist.
   */
  const getFirstErrorStep = () => VALIDATION_STEP_ORDER.find(hasStepErrors);

  /**
   * Determines if a step is enabled (can be clicked/navigated to).
   * Previous steps are always enabled, but subsequent steps are disabled
   * if any previous step has errors.
   */
  const isStepEnabled = (step: ValidationStep) => {
    const stepIndex = VALIDATION_STEP_ORDER.indexOf(step);

    // Check if all previous steps have no errors
    return !VALIDATION_STEP_ORDER.slice(0, stepIndex).find(hasStepErrors);
  };

  /**
   * Determines if the user can certify the report.
   * Certification is only allowed if there are no validation errors in any step.
   */
  const canCertify = Object.values(errors).every(isEmpty);

  /**
   * Returns insights on errors preventing certification
   */
  const getCertifyErrorText = () => {
    const alertError = Object.values(errors.Alerts ?? {})[0];
    if (alertError) return alertError.message;

    const firstErrorStep = getFirstErrorStep();
    if (firstErrorStep) {
      return `Please fill out all the required fields in the ${VALIDATION_STEP_LABELS[firstErrorStep]} tab before certifying the report.`;
    }
    return undefined;
  };

  return {
    isStepEnabled,
    hasStepErrors,
    getFirstErrorStep,
    getCertifyErrorText,
    canCertify,
  };
};
